import React, { useState } from 'react';
import { db, storage } from '../../config/firebase';
import { collection, addDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { motion } from 'framer-motion';
import './BusinessAdvertising.css';

const StatusMessage = ({ message, type }) => {
  if (!message) return null;
  
  return (
    <div className={`status-message ${type}`}>
      <div className="status-icon">
        {type === 'success' ? (
          <i className="fas fa-check-circle"></i>
        ) : (
          <i className="fas fa-exclamation-circle"></i>
        )}
      </div>
      <div className="status-text">{message}</div>
    </div>
  );
};

const StatusOverlay = ({ message, type, onClose }) => {
  return (
    <motion.div 
      className="mobile-status-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <div className="mobile-status-content">
        <div className={`status-icon ${type}`}>
          {type === 'success' ? (
            <i className="fas fa-check-circle"></i>
          ) : (
            <i className="fas fa-exclamation-circle"></i>
          )}
        </div>
        <p>{message}</p>
        <small>Tap anywhere to close</small>
      </div>
    </motion.div>
  );
};

const BusinessAdvertising = () => {
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [formData, setFormData] = useState({
    companyName: '',
    description: '',
    contactPerson: '',
    email: '',
    phone: '',
    package: ''
  });
  const [logo, setLogo] = useState(null);
  const [submitStatus, setSubmitStatus] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showStatusOverlay, setShowStatusOverlay] = useState(false);

  const packages = [
    {
      name: 'Premium',
      duration: '4 Weeks',
      price: 'R999',
      features: [
        'Premium Banner Placement',
        'Targeted Local Exposure',
        'Performance Analytics'
      ],
      description: 'Premium advertising package for 1 month',
      bestValue: true,
      pricePerWeek: 'Only R250/week'
    },
    {
      name: 'Advanced',
      duration: '2 weeks',
      price: 'R549',
      features: [
        'Premium Banner Placement',
        'Targeted Local Exposure',
        'Performance Analytics'
      ],
      description: 'Advanced advertising package for 2 weeks',
      pricePerWeek: 'R275/week'
    },
    {
      name: 'Basic',
      duration: '1 week',
      price: 'R299',
      features: [
        'Premium Banner Placement',
        'Targeted Local Exposure',
        'Performance Analytics'
      ],
      description: 'Basic advertising package for 1 week',
      pricePerWeek: 'R299/week'
    }
  ];

  const handlePackageSelect = (packageName) => {
    setSelectedPackage(packageName);
    setFormData(prev => ({ ...prev, package: packageName }));
    
    // Add subtle scroll to hint at the form below
    setTimeout(() => {
      const formElement = document.querySelector('.advertising-form-container');
      if (formElement) {
        const yOffset = -350; // This will show just a peek of the form
        const y = formElement.getBoundingClientRect().top + window.pageYOffset + yOffset;
        
        window.scrollTo({
          top: y,
          behavior: 'smooth'
        });
      }
    }, 100);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB limit

    if (file) {
      if (file.size > MAX_FILE_SIZE) {
        setSubmitStatus('File size must be less than 2MB');
        e.target.value = ''; // Reset file input
        return;
      }

      if (!file.type.match('image.*')) {
        setSubmitStatus('Please upload an image file (PNG, JPG, GIF)');
        e.target.value = '';
        return;
      }

      // Convert image to base64 string
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogo(e.target.result); // Store base64 string
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('');

    try {
      // Validation checks
      if (!selectedPackage) {
        setIsSubmitting(false);
        setSubmitStatus('Please select a package first');
        return;
      }
      if (!formData.companyName.trim()) {
        setIsSubmitting(false);
        setSubmitStatus('Company name is required');
        return;
      }
      if (!formData.email.trim()) {
        setIsSubmitting(false);
        setSubmitStatus('Email is required');
        return;
      }
      if (!formData.phone.trim()) {
        setIsSubmitting(false);
        setSubmitStatus('Phone number is required');
        return;
      }

      // Create the notification data with base64 logo
      const submissionData = {
        ...formData,
        logoImage: logo, // base64 string of the image
        type: 'advertising',
        status: 'pending',
        timestamp: new Date(),
        createdAt: new Date(),
        metadata: {
          source: 'web',
          version: '1.0'
        }
      };

      // Add to businessRequests collection
      const docRef = await addDoc(collection(db, 'advertisingRequests'), submissionData);
      
      if (docRef.id) {
        setSubmitStatus('Successfully submitted! Your advertising request is being reviewed.');
        setShowStatusOverlay(true);
        
        // Reset form after successful submission
        setTimeout(() => {
          setFormData({
            companyName: '',
            description: '',
            contactPerson: '',
            email: '',
            phone: '',
            package: ''
          });
          setLogo(null);
          setSelectedPackage(null);
        }, 3000);
      } else {
        throw new Error('Failed to get document reference');
      }

    } catch (error) {
      console.error('Submission Error:', error);
      setSubmitStatus(error.message || 'Error submitting form. Please try again.');
      setShowStatusOverlay(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="advertising-section">
      <h1>Maximize Your Business Visibility</h1>
      <p className="subtitle">Reach thousands of potential customers in your local area</p>

      <div className="benefits-grid">
        <div className="benefit-card">
          <div className="icon">
            <i className="fas fa-chart-line"></i>
          </div>
          <h3>High Engagement</h3>
          <p>Over 2,500 monthly active users searching for local services</p>
        </div>

        <div className="benefit-card">
          <div className="icon">
            <i className="fas fa-bullseye"></i>
          </div>
          <h3>Targeted Reach</h3>
          <p>Connect with customers actively looking for services in your area</p>
        </div>

        {/* <div className="benefit-card">
          <div className="icon">
            <i className="fas fa-users"></i>
          </div>
          <h3>Growing Community</h3>
          <p>Join our network of 500+ verified local businesses</p>
        </div> */}

        <div className="benefit-card">
          <div className="icon">
            <i className="fas fa-star"></i>
          </div>
          <h3>Priority Search</h3>
          <p>Appear first in search results with our priority listing feature</p>
        </div>
      </div>

      <div className="info-box">
        <div className="info-icon">
          <i className="fas fa-info-circle"></i>
        </div>
        <div className="info-content">
          <h4>Did You Know?</h4>
          <p>Businesses that advertise with us see an average of 40% increase in customer inquiries. Don't miss out on growing your business presence!</p>
          <p>With paid advertising, you not only get premium banner placement and targeted exposure but also priority search by appearing first on the list.</p>
        </div>
      </div>

      <h2 className="packages-title">Choose Your Visibility Package</h2>
      <div className="packages-grid">
        {packages.map((pkg) => (
          <div 
            key={pkg.name}
            className={`package-card ${selectedPackage === pkg.name ? 'selected' : ''} ${pkg.bestValue ? 'best-value' : ''}`}
            onClick={() => handlePackageSelect(pkg.name)}
          >
            {pkg.bestValue && <div className="best-value-tag">Best Value</div>}
            <h3>{pkg.name}</h3>
            <p className="duration">{pkg.duration}</p>
            <h2 className="price">{pkg.price}</h2>
            <p className="price-per-week">{pkg.pricePerWeek}</p>
            <p className="description">{pkg.description}</p>
            <ul className="features">
              {pkg.features.map((feature, index) => (
                <li key={index}>✓ {feature}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {selectedPackage && (
        <motion.div 
          className="advertising-form-container"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {showSuccessMessage ? (
            <motion.div 
              className="success-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <div className="success-content">
                <div className="success-icon">
                  <i className="fas fa-check-circle"></i>
                </div>
                <h3>Thank You!</h3>
                <p>Your advertising request has been successfully submitted.</p>
                <p>We will review your application and get back to you soon.</p>
              </div>
            </motion.div>
          ) : (
            <>
              <h2>Complete Your Advertising Request</h2>
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>Company Name</label>
                  <input
                    type="text"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Company Logo</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoChange}
                  />
                  <p className="file-requirements">
                    Maximum file size: 2MB. Supported formats: PNG, JPG, GIF
                  </p>
                </div>

                <div className="form-group">
                  <label>Advertisement Description</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Contact Person</label>
                  <input
                    type="text"
                    name="contactPerson"
                    value={formData.contactPerson}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <button 
                  type="submit" 
                  className={`submit-btn ${isSubmitting ? 'loading' : ''}`}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Advertisement Request'}
                </button>

                {submitStatus && (
                  <StatusMessage 
                    message={submitStatus} 
                    type={submitStatus.includes('Successfully') ? 'success' : 'error'} 
                  />
                )}
              </form>
            </>
          )}
        </motion.div>
      )}

      {showStatusOverlay && (
        <StatusOverlay 
          message={submitStatus}
          type={submitStatus.includes('Successfully') ? 'success' : 'error'}
          onClose={() => setShowStatusOverlay(false)}
        />
      )}
    </div>
  );
};

export default BusinessAdvertising; 